import { useParams, Link } from 'react-router-dom';
import { useSEO } from '../../../utils/seo';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string[];
  date: string;
  author: string;
  authorBio: string;
  category: string;
  image: string;
  slug: string;
  readTime: string;
  tags: string[];
}

const blogPostsData: Record<string, BlogPost> = {
  'breaking-barriers-mentorship-transforms-latina-professionals': {
    id: '1',
    title: 'Breaking Barriers: How Mentorship Transforms Latina Professionals',
    excerpt: 'Discover how mentorship programs are creating pathways for Latina women to break through glass ceilings and achieve leadership positions in their careers.',
    content: [
      'In today\'s competitive professional landscape, mentorship has emerged as a powerful catalyst for career advancement, particularly for Latina women navigating corporate environments. The impact of having a mentor who understands your cultural background and professional challenges cannot be overstated.',
      'Research shows that Latina professionals with mentors are 56% more likely to advance to leadership positions within five years compared to those without mentorship support. This statistic highlights the transformative power of guidance, advocacy, and strategic career planning that mentorship provides.',
      'At MasDeUna, we\'ve witnessed countless success stories where mentorship has been the turning point in a professional\'s career. From navigating difficult workplace dynamics to negotiating salary increases, mentors provide the wisdom and support needed to overcome obstacles that might otherwise seem insurmountable.',
      'One of the most significant benefits of mentorship is the expansion of professional networks. Mentors often open doors to opportunities that mentees might not have access to otherwise, including introductions to key industry leaders, recommendations for promotions, and insights into unadvertised job opportunities.',
      'Cultural understanding plays a crucial role in effective mentorship. Latina mentors bring unique perspectives on balancing cultural identity with professional ambitions, addressing imposter syndrome, and navigating workplace environments where representation may be limited.',
      'The mentorship relationship is not one-sided. Mentors also benefit from fresh perspectives, renewed passion for their work, and the satisfaction of contributing to the next generation of leaders. This reciprocal relationship creates a positive cycle of growth and empowerment within the Latina professional community.',
      'If you\'re considering seeking a mentor or becoming one, remember that the best mentorship relationships are built on trust, mutual respect, and clear communication. Set goals together, maintain regular contact, and be open to both giving and receiving feedback.',
      'The future of Latina leadership depends on our commitment to lifting each other up. Through mentorship, we create pathways for success that benefit not just individuals, but entire communities and organizations.'
    ],
    date: '2025-01-15',
    author: 'Maria Rodriguez',
    authorBio: 'Maria is a senior executive coach and MasDeUna mentor with over 15 years of experience in corporate leadership development.',
    category: 'Mentorship',
    image: 'https://public.readdy.ai/ai/img_res/b93fb0e7cf25b8d5a91587406aee86f7.jpg',
    slug: 'breaking-barriers-mentorship-transforms-latina-professionals',
    readTime: '5 min read',
    tags: ['Mentorship', 'Career Growth', 'Leadership', 'Professional Development']
  },
  'power-of-community-building-networks': {
    id: '2',
    title: 'The Power of Community: Building Networks That Elevate',
    excerpt: 'Learn how building strong professional networks within the Latina community creates opportunities for growth, collaboration, and mutual success.',
    content: [
      'Professional networking is often cited as one of the most important factors in career success, yet many professionals struggle to build meaningful connections. For Latina women, creating and nurturing a strong professional network within our community can be particularly transformative.',
      'The concept of "comadres" extends beautifully into the professional world. Just as we support each other in our personal lives, building a network of professional comadres creates a foundation of mutual support, shared opportunities, and collective growth.',
      'Effective networking goes beyond collecting business cards or LinkedIn connections. It\'s about building authentic relationships based on shared values, mutual respect, and genuine interest in each other\'s success. These relationships become the foundation for collaboration, referrals, and support during challenging times.',
      'One powerful aspect of community networking is the creation of safe spaces where Latina professionals can share experiences, challenges, and strategies without fear of judgment. These spaces allow for vulnerability and honest conversations about topics like workplace discrimination, work-life balance, and cultural identity.',
      'MasDeUna events and workshops provide structured opportunities for networking, but the real magic happens in the organic connections that form. We\'ve seen partnerships emerge, businesses launch, and careers transform through connections made within our community.',
      'Digital networking has expanded our reach, allowing Latina professionals across different cities and industries to connect and support each other. Social media platforms, professional groups, and virtual events have made it easier than ever to build a diverse and supportive network.',
      'Remember that networking is a two-way street. The most successful networkers are those who approach relationships with a mindset of giving rather than just receiving. Share your knowledge, make introductions, and celebrate others\' successes.',
      'As you build your network, focus on quality over quantity. A smaller network of engaged, supportive connections is far more valuable than a large network of superficial relationships. Invest time in nurturing the relationships that matter most.'
    ],
    date: '2025-01-12',
    author: 'Carmen Silva',
    authorBio: 'Carmen is a community organizer and networking strategist who has helped hundreds of Latina professionals build meaningful career connections.',
    category: 'Community',
    image: 'https://readdy.ai/api/search-image?query=diverse%20group%20of%20latina%20professionals%20networking%20at%20community%20event%20with%20warm%20collaborative%20atmosphere%20and%20professional%20setting&width=1200&height=600&seq=blogpost2&orientation=landscape',
    slug: 'power-of-community-building-networks',
    readTime: '4 min read',
    tags: ['Networking', 'Community', 'Professional Growth', 'Collaboration']
  },
  'student-to-leader-mas-academy-success-stories': {
    id: '3',
    title: 'From Student to Leader: Success Stories from MAS Academy',
    excerpt: 'Read inspiring stories from MAS Academy graduates who have transformed their careers through our workshops and mentorship programs.',
    content: [
      'The journey from student to leader is rarely linear, but with the right support and resources, it\'s absolutely achievable. MAS Academy has been privileged to witness and support countless transformations as participants develop the skills and confidence needed to step into leadership roles.',
      'Take Sofia\'s story, for example. She joined MAS Academy as a mid-level manager feeling stuck in her career. Through our leadership workshop series and one-on-one mentorship, she developed the strategic thinking and communication skills needed to advocate for herself. Within 18 months, she was promoted to director level, leading a team of 25 professionals.',
      'What makes these transformations possible? It starts with creating a learning environment where participants feel safe to take risks, ask questions, and challenge themselves. Our workshops are designed not just to impart knowledge, but to build confidence and practical skills that can be immediately applied in the workplace.',
      'Another graduate, Gabriela, came to us seeking to transition from a technical role into management. She had the expertise but lacked confidence in her leadership abilities. Through our workshops on emotional intelligence, team management, and strategic communication, she developed the skills needed to make the transition successfully.',
      'The power of peer learning cannot be underestimated. Many of our most successful graduates cite the connections they made with other participants as equally valuable as the workshop content itself. These relationships provide ongoing support, accountability, and opportunities for collaboration long after the workshops end.',
      'MAS Academy workshops cover a wide range of topics, from technical skills like data analysis and project management to soft skills like negotiation, public speaking, and executive presence. This comprehensive approach ensures that participants develop well-rounded leadership capabilities.',
      'We also emphasize the importance of cultural intelligence and authentic leadership. Our graduates learn to lead in ways that honor their cultural identity while navigating diverse professional environments. This authenticity becomes a source of strength and differentiation in their leadership style.',
      'The success of our graduates inspires us to continue expanding our curriculum and reaching more Latina professionals. Every success story reinforces our belief that with the right support, education, and community, there are no limits to what we can achieve.'
    ],
    date: '2025-01-10',
    author: 'Isabella Martinez',
    authorBio: 'Isabella is the Director of MAS Academy and has designed curriculum that has helped over 500 Latina professionals advance their careers.',
    category: 'Success Stories',
    image: 'https://readdy.ai/api/search-image?query=successful%20latina%20professional%20woman%20presenting%20at%20workshop%20with%20confident%20posture%20in%20bright%20modern%20training%20room%20with%20engaged%20audience&width=1200&height=600&seq=blogpost3&orientation=landscape',
    slug: 'student-to-leader-mas-academy-success-stories',
    readTime: '6 min read',
    tags: ['Success Stories', 'MAS Academy', 'Leadership', 'Career Transformation']
  }
};

const relatedPosts = [
  {
    title: 'Leadership Skills Every Latina Professional Should Master',
    slug: 'leadership-skills-latina-professionals',
    image: 'https://readdy.ai/api/search-image?query=confident%20latina%20business%20leader%20presenting%20to%20team%20in%20modern%20boardroom%20with%20professional%20atmosphere%20and%20natural%20lighting&width=400&height=250&seq=related1&orientation=landscape',
    category: 'Leadership'
  },
  {
    title: 'Navigating Career Transitions: A Guide for Latina Professionals',
    slug: 'navigating-career-transitions-guide',
    image: 'https://readdy.ai/api/search-image?query=latina%20professional%20woman%20planning%20career%20path%20with%20documents%20and%20laptop%20in%20bright%20modern%20office%20with%20motivational%20atmosphere&width=400&height=250&seq=related2&orientation=landscape',
    category: 'Career Development'
  },
  {
    title: 'The Impact of Representation: Why Latina Mentors Matter',
    slug: 'the-impact-of-representation-latina-mentors',
    image: 'https://readdy.ai/api/search-image?query=latina%20mentor%20and%20mentee%20having%20meaningful%20conversation%20in%20professional%20office%20setting%20with%20warm%20supportive%20atmosphere&width=400&height=250&seq=related3&orientation=landscape',
    category: 'Mentorship'
  }
];

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? blogPostsData[slug] : null;

  // Move useSEO hook before any conditional returns
  useSEO({
    title: post ? `${post.title} | MasDeUna Blog` : 'Post Not Found | MasDeUna Blog',
    description: post ? post.excerpt : 'The blog post you are looking for could not be found.',
    keywords: post ? `${post.tags.join(', ')}, Latina professionals, career development, MasDeUna` : 'MasDeUna, blog',
    schema: post ? {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      image: post.image,
      datePublished: post.date,
      author: {
        '@type': 'Person',
        name: post.author
      },
      publisher: {
        '@type': 'Organization',
        name: 'MasDeUna',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.masdeuna.org/logo.png'
        }
      }
    } : undefined
  });

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-20 px-6 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-teal-600 hover:text-teal-700 font-medium">
            ← Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <article className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back Link */}
          <Link to="/blog" className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium mb-8 cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Blog
          </Link>

          {/* Category & Read Time */}
          <div className="flex items-center gap-4 mb-6">
            <span className="px-4 py-1 bg-teal-500 text-white text-sm font-medium rounded-full">
              {post.category}
            </span>
            <span className="text-gray-500">{post.readTime}</span>
          </div>

          {/* Title */}
          <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {post.title}
          </h1>

          {/* Author & Date */}
          <div className="flex items-center gap-4 mb-8 pb-8 border-b border-gray-200">
            <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">{post.author.charAt(0)}</span>
            </div>
            <div>
              <div className="font-medium text-gray-900">{post.author}</div>
              <time className="text-sm text-gray-500">
                {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </time>
            </div>
          </div>

          {/* Featured Image */}
          <div className="w-full h-96 rounded-2xl overflow-hidden mb-12">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            {post.content.map((paragraph, index) => (
              <p key={index} className="text-gray-700 leading-relaxed mb-6">
                {paragraph}
              </p>
            ))}
          </div>

          {/* Tags */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {/* Author Bio */}
          <div className="mt-12 p-8 bg-gray-50 rounded-2xl">
            <div className="flex items-start gap-6">
              <div className="w-20 h-20 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-2xl">{post.author.charAt(0)}</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">About {post.author}</h3>
                <p className="text-gray-600 leading-relaxed">{post.authorBio}</p>
              </div>
            </div>
          </div>

          {/* Share Section */}
          <div className="mt-12 text-center">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Share this article</h3>
            <div className="flex justify-center gap-4">
              <button className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                <i className="ri-twitter-x-line text-xl"></i>
              </button>
              <button className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                <i className="ri-facebook-fill text-xl"></i>
              </button>
              <button className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                <i className="ri-linkedin-fill text-xl"></i>
              </button>
              <button className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                <i className="ri-mail-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Related Posts */}
        <section className="mt-20 max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Related Articles</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {relatedPosts.map((relatedPost) => (
              <Link
                key={relatedPost.slug}
                to={`/blog/${relatedPost.slug}`}
                className="group"
              >
                <div className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300">
                  <div className="w-full h-48 overflow-hidden">
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <span className="text-teal-600 text-sm font-medium">{relatedPost.category}</span>
                    <h3 className="text-lg font-bold text-gray-900 mt-2 group-hover:text-teal-600 transition-colors">
                      {relatedPost.title}
                    </h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </article>

      <Footer />
    </div>
  );
}
